import { __ } from '@wordpress/i18n';

import clsx from 'clsx';

import { useEntityProp, store as coreStore } from '@wordpress/core-data';
import { useSelect } from '@wordpress/data';
import { useBlockProps, AlignmentControl, InspectorControls, BlockControls, RichText } from '@wordpress/block-editor';
import { PanelBody, SelectControl } from '@wordpress/components';

export default function Edit(props) {
    const {
        attributes,
    	setAttributes,
        context: { postId, postType, queryId }
    } = props;

    const { textAlign, testimonialPostId } = attributes;

    // Are we editing a Showcase post?
    const isShowcaseContext = postType === 'gwx_testimonial' && !!postId;

    // Which post's data should we display?
    const resolvedPostId = isShowcaseContext ? postId : (testimonialPostId || null);
    
    const blockProps = useBlockProps({
        className: clsx( {
			[ `has-text-align-${ textAlign }` ]: textAlign,
		} )
    });

    const TagName = 'p';

    const isDescendentOfQueryLoop = Number.isFinite( queryId );
    const userCanEdit = useSelect(
		( select ) => {
			if ( isDescendentOfQueryLoop ) {
				return false;
			}
			return select( coreStore ).canUser( 'update', {
				kind: 'postType',
				name: postType,
				id: postId,
			} );
		},
		[ isDescendentOfQueryLoop, postType, postId ]
	);

    // Retrieve meta values
    const [ meta, setMeta ] = useEntityProp('postType', postType, 'meta', postId);
    
    let authorContent = <TagName { ...blockProps }>{ __( 'Author', 'groundworx-testimonial' ) }</TagName>;

    const testimonialOptions = useSelect((select) => {
        const { getEntityRecords } = select('core');
        const posts = getEntityRecords('postType', 'gwx_testimonial', { per_page: -1 }) || [];
        return posts.map((post) => ({
            label: post.title.rendered || `#${post.id}`,
            value: post.id,
        }));
    }, []);

    if (postType && postId && postType === 'gwx_testimonial') {

        const { gwx_author = '' } = meta || {};
        authorContent = (
            <RichText
                tagName = { TagName }
                value = { gwx_author || ''}
                onChange = {(value) => setMeta({ ...meta, gwx_author: value })}
                placeholder = {__('Enter author', 'groundworx-testimonial')}
                { ...blockProps }
            />
        );
    }

    if (postType && postId && postType !== 'gwx_testimonial') {
        const resolvedPostId = postType === 'gwx_testimonial' ? postId : testimonialPostId;

        const selectedMeta = useSelect((select) => {
            if (!resolvedPostId) return null;

            const { getEntityRecord } = select('core');
            return getEntityRecord('postType', 'gwx_testimonial', resolvedPostId);
        }, [resolvedPostId]);
        
        if (selectedMeta && selectedMeta.meta && selectedMeta.meta.gwx_author) {
            authorContent = ( <TagName { ...blockProps } dangerouslySetInnerHTML={{ __html: selectedMeta.meta.gwx_author }} /> );
        }
    }
    
    return (
        <>
            <BlockControls group="block">
                <AlignmentControl
                    value={ textAlign }
                    onChange={ ( newTextAlign ) =>
                        setAttributes( {
                            textAlign: newTextAlign
                        } )
                    }
                />
            </BlockControls>
            {postType !== 'gwx_testimonial' && (
                <InspectorControls>
                    <PanelBody title={__('Testimonial Settings', 'groundworx-testimonial')}>
                        
                            <SelectControl
                                label={__('Select Testimonial', 'groundworx-testimonial')}
                                value={attributes.testimonialPostId || ''}
                                options={[
                                    { label: __('Select…', 'groundworx-testimonial'), value: '' },
                                    ...testimonialOptions
                                ]}
                                onChange={(value) =>
                                    setAttributes({ testimonialPostId: parseInt(value, 10) || null })
                                }
                            />
                        
                    </PanelBody>
                </InspectorControls>
            )}
            
            { !resolvedPostId && userCanEdit ? (
                <SelectControl
                    label={__('Select a testimonial author to display', 'groundworx-testimonial')}
                    value={attributes.testimonialPostId || ''}
                    options={ [ { label: __( 'Select…', 'groundworx-testimonial' ), value: '' }, ...testimonialOptions ] }
                    onChange={(value) => {
                        setAttributes({ testimonialPostId: parseInt(value, 10) });
                    }}
                />
            ) : (
                authorContent
            )}
            
        </>
    );
}